import useApi, { UseApiConfig } from 'common/hooks/useApi';
import useThunkDispatch from 'common/hooks/useThunkDispatch';
import useTypedSelector from 'common/hooks/useTypedSelector';
import AlertType from 'common/types/AlertType';
import { Paginated } from 'common/types/Paginated';
import { ApplicationStatus } from 'domain/components/application/ApplicationStatus';
import RejectionTypeCd from 'domain/enums/RejectionTypeCd';
import { Application } from 'domain/types/Application';
import { loadProfile } from 'features/profile-settings/ProfileSlice';
import queryString from 'querystring';
import { useEffect } from 'react';
import { useHistory } from 'react-router';
import AccountDashboardPresenter from './AccountDashboardPresenter';
import hasUploadedIdentity from './suggested-actions/identity';
import hasMailingAddress from './suggested-actions/mailing-address';
import { isRejected } from './suggested-actions/rejections';

type PrecheckSearchResponse = Paginated<{ items: Application[] }>;
const precheckSearchConfig = (
  rejectionTypeCd: RejectionTypeCd,
  pageSize: number
) => {
  const params = queryString.stringify({
    pageNum: 0,
    pageSize,
    statusCd: ApplicationStatus.PRECHECK_FAILED,
    rejectionTypeCd,
  });
  return {
    url: `/applications?${params}`,
    method: 'GET',
  } as UseApiConfig;
};

function AccountDashboardContainer() {
  const { name: profileName, mailing, identity } = useTypedSelector(
    (state) => ({
      name: state.profile.data.firstName,
      applications: state.applications,
      mailing: hasMailingAddress(state.profile.data.address),
      identity: hasUploadedIdentity(
        state.profile.data.identity,
        state.profile.data.verification
      ),
    })
  );

  const { push, location } = useHistory<{ message: string; type: AlertType }>();

  const dispatch = useThunkDispatch();

  // Load profile verification statuses
  useEffect(() => {
    dispatch(loadProfile());
  }, [dispatch]);

  const [
    { data: failedIdentityPage },
    fetchFailedIdentity,
  ] = useApi<PrecheckSearchResponse>(
    precheckSearchConfig(RejectionTypeCd.IDENTITY, 1)
  );
  const [
    { data: failedShuolPage },
    fetchFailedShuol,
  ] = useApi<PrecheckSearchResponse>(
    precheckSearchConfig(RejectionTypeCd.SHUOL, 1)
  );
  const [
    { data: failedPrimaryResPage },
    fetchFailedPrimaryRes,
  ] = useApi<PrecheckSearchResponse>(
    precheckSearchConfig(RejectionTypeCd.PRIMARY_RESIDENCE, 3)
  );

  useEffect(() => {
    fetchFailedIdentity();
  }, [fetchFailedIdentity]);

  useEffect(() => {
    fetchFailedShuol();
  }, [fetchFailedShuol]);

  useEffect(() => {
    fetchFailedPrimaryRes();
  }, [fetchFailedPrimaryRes]);

  return (
    <AccountDashboardPresenter
      name={profileName}
      hasMailingAddress={mailing}
      hasUploadedIdentity={identity}
      message={
        Boolean(location.state?.message && location.state?.type)
          ? {
              text: location.state.message,
              type: location.state.type,
            }
          : undefined
      }
      hasRejectedShuol={Boolean(
        failedShuolPage?.items.some((a) => isRejected(a, RejectionTypeCd.SHUOL))
      )}
      hasRejectedIdentity={Boolean(
        failedIdentityPage?.items.some((a) =>
          isRejected(a, RejectionTypeCd.IDENTITY)
        )
      )}
      primaryResDeniedApps={failedPrimaryResPage?.items || []}
      onNavigate={(to) => push(to)}
    />
  );
}

export default AccountDashboardContainer;
